import React from 'react';

// "Better or worse than last period?", in one badge.
//
// Extracted from AnalyticsPage rather than copied to the Dashboard: both screens
// answer the same question from the same served field (`comparison`, the previous
// equal-length window), and a rule stated twice eventually gets stated
// differently — the same reason `utils/funnel-units.ts` exists one file over.
//
// THE BADGE MUST COMPARE AGAINST THE NUMBER RENDERED BESIDE IT. The two screens
// arrive at `current` differently on purpose: Analytics passes the server's
// `total*`, the Dashboard sums `daily`. In production those agree (the totals and
// the daily series read the same tables with the same ownership predicates and
// bounds, and `matches_served_daily` holds at most one row per user and day), but
// where they can diverge the badge belongs to the number the reader can see, not
// to a number the screen never shows. So `current` is passed in by the caller and
// never fetched here.
export interface DeltaBadgeProps {
  current: number;
  /** The same quantity over the previous equal-length window. */
  previous: number;
}

/**
 * Signed delta vs the previous equal period, with safe zero-edges:
 * prev 0 & cur 0 → none; prev 0 & cur>0 → "new"; prev>0 & cur 0 → −100%.
 *
 * The zero edges are not decoration: three of the four production accounts have
 * zeros in every one of these series, so "no previous data" is the common case
 * and must not render as a division by zero or a misleading +∞.
 */
const DeltaBadge: React.FC<DeltaBadgeProps> = ({ current, previous }) => {
  if (previous === 0 && current === 0) return <span style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>—</span>;
  if (previous === 0) return <span style={{ color: 'var(--good)', fontSize: '0.72rem', fontWeight: 600 }}>new</span>;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up = pct >= 0;
  return (
    <span style={{ color: up ? 'var(--good)' : 'var(--danger)', fontSize: '0.72rem', fontWeight: 600 }}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{pct}%
    </span>
  );
};

export default DeltaBadge;
